import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../config";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Input } from "./ui/input";
import { Archive, RefreshCw, FileText, Calendar, Globe, Brain, ArrowRight, ExternalLink, Search } from "lucide-react";
import ProUpsellCard from "./ProUpsellCard";

const ArchiveModal = ({ darkMode, authHeader, triggerTestId = "archive-modal-trigger" }) => {
  const [archiveStats, setArchiveStats] = useState(null);
  const [archiveArticles, setArchiveArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState("stats");
  const [selectedTimePeriod, setSelectedTimePeriod] = useState("");
  const [selectedSource, setSelectedSource] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMeta, setSearchMeta] = useState(null);   // {matched_by, remaining_today}
  const [searchCapped, setSearchCapped] = useState(false);
  const dm = darkMode;

  // Mount-only: fetch archive statistics once on open
  useEffect(() => { fetchArchiveStats(); }, []);

  const fetchArchiveStats = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/archive/statistics`, { headers: authHeader() });
      setArchiveStats(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchBy = async (path) => {
    try {
      setLoading(true);
      setSearchMeta(null);
      const res = await axios.get(`${API}/archive/${path}`, { headers: authHeader() });
      setArchiveArticles(res.data.articles || []);
      setActiveView("articles");
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const runSearch = async () => {
    const q = searchQuery.trim();
    if (q.length < 2) return;
    try {
      setLoading(true);
      setSearchCapped(false);
      const res = await axios.get(`${API}/search`, { params: { q, limit: 20 }, headers: authHeader() });
      setArchiveArticles((res.data.results || []).map(r => ({ ...r, originalUrl: r.url })));
      setSearchMeta({ matched_by: res.data.matched_by, remaining_today: res.data.remaining_today });
      setActiveView("articles");
    } catch (e) {
      if (e?.response?.status === 429) { setSearchCapped(true); setActiveView("articles"); setArchiveArticles([]); setSearchMeta(null); }
      else console.error(e);
    }
    finally { setLoading(false); }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid={triggerTestId} className={`category-tag flex items-center gap-1.5 px-1 py-0.5 transition-colors ${dm ? "text-slate-400 hover:text-slate-100" : "text-[#6b7a7d] hover:text-[#191c1d]"}`}>
          <Archive className="w-3 h-3" />Archive
        </button>
      </DialogTrigger>
      <DialogContent className={`max-w-5xl max-h-[88vh] overflow-y-auto border-0 ${dm ? "bg-[#0c1011] text-slate-100" : "bg-white text-[#191c1d]"}`} style={{ boxShadow: "0 0 60px rgba(0,0,0,0.15)" }}>
        <DialogHeader className="pb-4">
          <DialogTitle className={`editorial-heading text-3xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>News Archive</DialogTitle>
          <DialogDescription className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>LIFO 3-MONTH ROLLING ARCHIVE</DialogDescription>
          <div className="flex gap-2 pt-3">
            <div className="relative flex-1">
              <Search className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${dm ? "text-slate-500" : "text-[#9aa5a6]"}`} />
              <Input data-testid="archive-search-input" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search the archive by meaning — e.g. 'hotels using AI for guest service'"
                onKeyPress={(e) => e.key === "Enter" && runSearch()}
                className={`border-0 pl-9 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`} />
            </div>
            <button data-testid="archive-search-btn" onClick={runSearch} disabled={loading || searchQuery.trim().length < 2}
              className={`px-5 rounded-full category-tag disabled:opacity-40 ${dm ? "bg-cyan-400 text-[#001f24]" : "bg-[#006874] text-white"}`}>
              Search
            </button>
          </div>
        </DialogHeader>
        <Tabs value={activeView} onValueChange={setActiveView} className="w-full">
          <TabsList className={`grid w-full grid-cols-3 rounded-xl p-1 ${dm ? "bg-[#1a2122]" : "bg-[#f3f4f5]"}`}>
            <TabsTrigger value="stats" className="category-tag rounded-lg">Statistics</TabsTrigger>
            <TabsTrigger value="browse" className="category-tag rounded-lg">Browse</TabsTrigger>
            <TabsTrigger value="articles" className="category-tag rounded-lg">Articles</TabsTrigger>
          </TabsList>
          <TabsContent value="stats" className="space-y-6 mt-6">
            {loading ? <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-[#006874]" /></div>
              : archiveStats ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { icon: FileText, label: "Total Articles", value: archiveStats.total_articles, color: "text-[#006874]" },
                      { icon: Calendar, label: "Archive Months", value: archiveStats.archive_months, color: "text-emerald-600" },
                      { icon: Globe, label: "Sources", value: archiveStats.source_stats?.length || 0, color: "text-amber-600" },
                      { icon: Brain, label: "AI Topics", value: archiveStats.topic_stats?.length || 0, color: "text-violet-600" },
                    ].map(({ icon: Icon, label, value, color }) => (
                      <div key={label} className={`rounded-xl p-5 ${dm ? "bg-[#1a2122]" : "bg-[#f3f4f5]"}`}>
                        <Icon className={`w-4 h-4 mb-3 ${color}`} />
                        <p className="editorial-heading text-2xl font-medium">{value}</p>
                        <p className={`category-tag mt-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{label}</p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className={`category-tag mb-4 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Top Sources</p>
                    <div className="grid grid-cols-2 gap-2">
                      {archiveStats.source_stats?.slice(0, 8).map((s) => (
                        <button key={s._id} onClick={() => fetchBy(`source/${encodeURIComponent(s._id)}`)} className={`rounded-xl p-4 text-left flex items-center justify-between group ${dm ? "bg-[#1a2122] hover:bg-[#252e2f]" : "bg-[#f3f4f5] hover:bg-[#e7e8e9]"}`}>
                          <div><p className={`text-sm font-medium truncate ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{s._id}</p><p className={`category-tag mt-0.5 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{s.count}</p></div>
                          <ArrowRight className={`w-3.5 h-3.5 opacity-0 group-hover:opacity-100 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className={`category-tag mb-4 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>AI-Extracted Topics</p>
                    <div className="flex flex-wrap gap-2">
                      {archiveStats.topic_stats?.slice(0, 15).map((t) => (
                        <button key={t._id} onClick={() => fetchBy(`topic/${encodeURIComponent(t._id)}`)} className={`px-3 py-1.5 rounded-full category-tag transition-colors ${dm ? "bg-[#1a2122] text-cyan-400/80 hover:bg-[#252e2f]" : "bg-[#f3f4f5] text-[#006874] hover:bg-[#e7e8e9]"}`}>
                          {t._id.replace("_", " ")} ({t.count})
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : <p className="text-center py-12 category-tag">No data</p>}
          </TabsContent>
          <TabsContent value="browse" className="space-y-4 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { label: "By Time", placeholder: "month:2025-01", state: selectedTimePeriod, set: setSelectedTimePeriod, path: () => `time/${selectedTimePeriod}` },
                { label: "By Source", placeholder: "Google News", state: selectedSource, set: setSelectedSource, path: () => `source/${encodeURIComponent(selectedSource)}` },
                { label: "By Topic", placeholder: "artificial intelligence", state: selectedTopic, set: setSelectedTopic, path: () => `topic/${encodeURIComponent(selectedTopic)}` },
              ].map(({ label, placeholder, state, set, path }) => (
                <div key={label} className={`rounded-xl p-5 ${dm ? "bg-[#1a2122]" : "bg-[#f3f4f5]"}`}>
                  <p className={`text-sm font-semibold mb-3 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{label}</p>
                  <Input value={state} onChange={(e) => set(e.target.value)} placeholder={placeholder}
                    onKeyPress={(e) => e.key === "Enter" && state && fetchBy(path())}
                    className={`border-0 mb-3 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-white"}`} />
                  <button onClick={() => state && fetchBy(path())} disabled={!state} className={`w-full py-2 rounded-full category-tag disabled:opacity-40 ${dm ? "bg-cyan-400/10 text-cyan-400 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}>Search</button>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="articles" className="mt-6">
            {loading ? <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-[#006874]" /></div>
              : searchCapped ? (
                <div className="py-8 space-y-4" data-testid="search-cap-panel">
                  <p className={`text-center category-tag ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>Daily search limit reached</p>
                  <ProUpsellCard surface="archive_search_cap" darkMode={dm} message="PRO: unlimited semantic search over the full archive." />
                </div>
              ) : archiveArticles.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <p className={`category-tag ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`} data-testid="archive-results-count">
                      {archiveArticles.length} results
                      {searchMeta && <span className="ml-2 opacity-70">· {searchMeta.matched_by === "semantic" ? "semantic match" : "text match"}{searchMeta.remaining_today != null ? ` · ${searchMeta.remaining_today} searches left today` : ""}</span>}
                    </p>
                    <button onClick={() => setActiveView("browse")} className={`category-tag ${dm ? "text-cyan-400" : "text-[#006874]"}`}>← Browse</button>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {archiveArticles.map((a) => (
                      <div key={a.id || a.originalUrl || a.title} className={`rounded-xl p-5 ${dm ? "bg-[#1a2122]" : "bg-[#f3f4f5]"}`}>
                        <p className={`editorial-heading text-base font-medium mb-2 ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>{a.title}</p>
                        <p className={`text-xs leading-relaxed mb-3 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>{a.summary}</p>
                        <div className="flex justify-between items-center">
                          <span className={`category-tag ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{a.source}{a.score != null ? ` · ${(a.score * 100).toFixed(0)}%` : ""}</span>
                          <button onClick={() => a.originalUrl && window.open(a.originalUrl, "_blank")} className={`category-tag flex items-center gap-1 ${dm ? "text-cyan-400" : "text-[#006874]"}`}>Read <ExternalLink className="w-3 h-3" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {searchMeta && searchMeta.remaining_today != null && (
                    <ProUpsellCard surface="archive_depth" darkMode={dm} message="PRO: unlimited search and full archive depth beyond the 3-month window." />
                  )}
                </div>
              ) : (
                <div className={`text-center py-16 ${dm ? "text-slate-600" : "text-[#6b7a7d]"}`}>
                  <Archive className="w-8 h-8 mx-auto mb-3 opacity-40" />
                  <p className="category-tag">Use Browse to search the archive</p>
                </div>
              )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ArchiveModal;
